import { useState, useEffect, useCallback } from "react";
import {
  Briefcase,
  PieChart,
  BarChart3,
  Calendar,
  Users,
  ThumbsUp,
  AlertTriangle,
  TrendingUp,
  Clock,
  FileText,
} from "lucide-react";
import { WidgetConfig } from "@/components/dashboard/DashboardCustomizer";
import React from "react";

const STORAGE_KEY = "dashboard_widgets_config";

const defaultWidgets: WidgetConfig[] = [
  // Stats
  {
    id: "stat-total-projects",
    name: "Total Projects",
    description: "Shows total number of projects",
    icon: React.createElement(Briefcase, { size: 18 }),
    category: "stats",
    enabled: true,
    size: "small",
  },
  {
    id: "stat-dd-progress",
    name: "Average Progress",
    description: "Average DD completion rate",
    icon: React.createElement(TrendingUp, { size: 18 }),
    category: "stats",
    enabled: true,
    size: "small",
  },
  {
    id: "stat-high-risk",
    name: "High Risk Projects",
    description: "Number of high risk projects",
    icon: React.createElement(AlertTriangle, { size: 18 }),
    category: "stats",
    enabled: true,
    size: "small",
  },
  {
    id: "stat-team",
    name: "Team Members",
    description: "Total users in the system",
    icon: React.createElement(Users, { size: 18 }),
    category: "stats",
    enabled: true,
    size: "small",
  },
  // Charts
  {
    id: "chart-status",
    name: "Status Distribution",
    description: "Pie chart of project statuses",
    icon: React.createElement(PieChart, { size: 18 }),
    category: "charts",
    enabled: true,
    size: "medium",
  },
  {
    id: "chart-phases",
    name: "Phase Overview",
    description: "Bar chart of projects by phase",
    icon: React.createElement(BarChart3, { size: 18 }),
    category: "charts",
    enabled: true,
    size: "large",
  },
  // Lists
  {
    id: "list-active-projects",
    name: "Active Projects",
    description: "List of active projects with quick access",
    icon: React.createElement(Briefcase, { size: 18 }),
    category: "lists",
    enabled: true,
    size: "large",
  }
];

export const useDashboardWidgets = () => {
  const [widgets, setWidgets] = useState<WidgetConfig[]>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Merge with defaults to handle new widgets
        return defaultWidgets.map((defaultWidget) => {
          const stored = parsed.find((w: WidgetConfig) => w.id === defaultWidget.id);
          return stored ? { ...defaultWidget, enabled: stored.enabled } : defaultWidget;
        });
      }
    } catch (e) {
      console.error("Failed to load widget config", e);
    }
    return defaultWidgets;
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(widgets));
  }, [widgets]);

  const updateWidgets = useCallback((newWidgets: WidgetConfig[]) => {
    setWidgets(newWidgets);
  }, []);

  const resetToDefault = useCallback(() => {
    setWidgets(defaultWidgets);
  }, []);

  const isWidgetEnabled = useCallback(
    (widgetId: string) => {
      const widget = widgets.find((w) => w.id === widgetId);
      return widget?.enabled ?? false;
    },
    [widgets]
  );

  return {
    widgets,
    updateWidgets,
    resetToDefault,
    isWidgetEnabled,
  };
};
